import { ReactJsonView } from 'react-scale/corp/components/ReactJsonView';
import React, { useState, useEffect, useCallback } from 'react';
import { ScrollArea, TextArea } from '@scale/scaleui-radix';
import { IJSONEditableProps } from '../types';

export default function JSONEditable({
  isShowingRaw,
  json,
  onEditJSON,
  preserveTypes = true,
}: IJSONEditableProps) {
  const [isJsonValid, setIsJsonValid] = useState(true);

  const onEditRawJSON = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    try {
      const text = e.target.value;
      const object = JSON.parse(text);

      onEditJSON(object);
      setIsJsonValid(true);
    } catch (error) {
      setIsJsonValid(false);
      console.error(error); // eslint-disable-line no-console
    }
  };

  const applyValue = (object: any, path: string[], name: string, value: any) => {
    let root = object;
    for (const field of path) {
      root = object[field];
    }
    root[name] = value;
  };

  const onEditInteractiveJSON = useCallback(
    ({ updated_src: src, name, namespace, new_value, existing_value }: any) => {
      if (!preserveTypes) {
        onEditJSON(src);
        return;
      }
      // HACK: Try to preserve types
      let typedNewValue = new_value;
      const asString = new_value?.toString();
      const asNumber = parseFloat(new_value?.toString().trim());
      switch (typeof existing_value) {
        case 'string':
          typedNewValue = asString;
          break;
        case 'number':
          if (!isNaN(asNumber)) {
            if (Math.round(asNumber) === asNumber) typedNewValue = Math.round(asNumber);
            else typedNewValue = asNumber;
          }
          break;
        case 'boolean':
          typedNewValue =
            asString?.trim() === 'true'
              ? true
              : asString?.trim() === 'false'
              ? false
              : existing_value;
          break;
        default:
          break;
      }
      applyValue(src, namespace, name, typedNewValue);

      onEditJSON(src);
    },
    [onEditJSON, preserveTypes],
  );

  useEffect(() => {
    setIsJsonValid(true);
  }, [isShowingRaw]);

  if (isShowingRaw) {
    return (
      <>
        {!isJsonValid && <span>This JSON is not valid. Please fix.</span>}
        <TextArea
          size="3"
          id="json-text"
          defaultValue={JSON.stringify(json, null, 4)}
          onChange={onEditRawJSON}
          style={{ minHeight: '480px' }}
        />
      </>
    );
  }

  return (
    <ScrollArea scrollbars="vertical" style={{ maxHeight: 520 }}>
      <ReactJsonView
        src={json}
        name={false}
        theme="monokai"
        iconStyle="triangle"
        collapsed={4}
        collapseStringsAfterLength={120}
        groupArraysAfterLength={false}
        displayObjectSize={false}
        displayDataTypes={false}
        onEdit={onEditInteractiveJSON}
        onAdd={onEditInteractiveJSON}
        onDelete={onEditInteractiveJSON}
      />

      <style jsx>{`
        :global(.react-json-view) {
          border-radius: 8px;
          padding: 12px;
          minheight: 550px;
        }
      `}</style>
    </ScrollArea>
  );
}
